import {useRef, useEffect, useState} from 'react'

// Cloudflare Turnstile CAPTCHA widget (same implementation as the admin login).
// Renders the challenge and calls onVerify(token) when solved, onExpire() when it lapses.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

export function TurnstileWidget({
  onVerify,
  onExpire,
  onError,
}: {
  onVerify: (token: string) => void
  onExpire: () => void
  onError?: (code: string) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onVerifyRef = useRef(onVerify)
  const onExpireRef = useRef(onExpire)
  const onErrorRef = useRef(onError)
  const renderedRef = useRef(false)
  // Cloudflare renders its own red "Verification failed / Troubleshoot" box when the
  // challenge can't run (600010 — in-app WebViews, old Android browsers, a skewed
  // device clock, DNS filtering). Since the challenge is advisory here and the form
  // submits without a token, that box is pure alarm for a visitor who did nothing
  // wrong — so hide it and let Cloudflare keep retrying out of sight.
  const [hidden, setHidden] = useState(false)

  onVerifyRef.current = onVerify
  onExpireRef.current = onExpire
  onErrorRef.current = onError

  useEffect(() => {
    if (renderedRef.current) return

    const doRender = () => {
      if (!containerRef.current || !(window as any).turnstile || renderedRef.current) return
      renderedRef.current = true
      widgetIdRef.current = (window as any).turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setHidden(false)
          onVerifyRef.current(token)
        },
        'expired-callback': () => onExpireRef.current(),
        // Without an error-callback a failed challenge (600010 and friends) is
        // terminal: the widget just sits on "Verification failed" and never
        // produces a token. Tell the caller so it can stop waiting on one, and
        // let Cloudflare retry the challenge by itself in the background.
        'error-callback': (code: string) => {
          console.warn('[turnstile] challenge error', code)
          setHidden(true)
          if (onErrorRef.current) onErrorRef.current(String(code || ''))
          // Returning true keeps the widget alive for the auto-retry below
          // instead of letting the script throw an uncaught TurnstileError.
          return true
        },
        retry: 'auto',
        'retry-interval': 4000,
        'refresh-expired': 'auto',
        // Only show the widget when Cloudflare actually needs the visitor to do
        // something. Visitors it clears silently — the large majority — never see
        // a captcha at all.
        appearance: 'interaction-only',
        theme: 'light',
      })
    }

    if ((window as any).turnstile) {
      doRender()
    } else {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit'
      script.async = true;
      (window as any).onTurnstileLoad = doRender
      document.head.appendChild(script)
    }

    return () => {
      if (widgetIdRef.current !== null) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch {}
        renderedRef.current = false
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className='mb-5 d-flex justify-content-center'
      style={hidden ? {display: 'none'} : undefined}
    />
  )
}

// True when a Turnstile site key is configured (so callers can gate the submit button).
export const TURNSTILE_ENABLED = !!TURNSTILE_SITE_KEY
